/**
 * BRContext — global Bamboo Rose sync state that persists across route changes.
 *
 * Provides:
 *  - syncing / progress / stepLabel / syncDone / syncError  — live sync status
 *  - brLatest  — metadata from the most recent export (timestamp, row count)
 *  - handleSync()  — trigger a new sync
 *  - registerVerify(poId, fn) / unregisterVerify()  — AuditPage registers its
 *    "open verify dialog" handler so the app header can surface the button.
 *  - verifyPoId / verifyHandler  — read by the header
 */
import React, {
  createContext, useContext, useState, useEffect, useRef, useCallback,
} from 'react';
import axios from '../api/client';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8004';

// Maps backend progress message fragments → approximate % + human label.
const STEP_MAP = [
  { match: 'launching browser',         pct: 5,   label: 'Starting browser…' },
  { match: 'navigating to bamboo',      pct: 12,  label: 'Opening Bamboo Rose…' },
  { match: 'checking login',            pct: 18,  label: 'Checking session…' },
  { match: 'login required',            pct: 20,  label: 'Logging in…' },
  { match: 'filling credentials',       pct: 23,  label: 'Filling credentials…' },
  { match: 'login successful',          pct: 30,  label: 'Logged in ✓' },
  { match: 'already logged in',         pct: 30,  label: 'Session active ✓' },
  { match: 'waiting for query',         pct: 38,  label: 'Running query…' },
  { match: 'detecting data frame',      pct: 42,  label: 'Finding data frame…' },
  { match: 'looking for export',        pct: 50,  label: 'Finding Export button…' },
  { match: 'clicking export',           pct: 58,  label: 'Clicking Export to Excel…' },
  { match: 'waiting for excel',         pct: 62,  label: 'Waiting for download…' },
  { match: 'waiting for excel export',  pct: 65,  label: 'Waiting for response…' },
  { match: '15s/',                      pct: 67,  label: 'Downloading… (15s)' },
  { match: '30s/',                      pct: 70,  label: 'Downloading… (30s)' },
  { match: '45s/',                      pct: 73,  label: 'Downloading… (45s)' },
  { match: 'response intercept got no', pct: 75,  label: 'Falling back to scraping…' },
  { match: 'scraping results page 1',   pct: 78,  label: 'Scraping page 1…' },
  { match: 'scraping results page',     pct: 82,  label: 'Scraping table…' },
  { match: 'downloaded via response',   pct: 85,  label: 'File downloaded ✓' },
  { match: 'parsing excel',             pct: 90,  label: 'Parsing Excel…' },
  { match: 'extracted',                 pct: 94,  label: 'Rows extracted ✓' },
  { match: 'scraped',                   pct: 94,  label: 'Rows scraped ✓' },
  { match: 'saving',                    pct: 96,  label: 'Saving to database…' },
  { match: 'done',                      pct: 100, label: 'Done ✓' },
];

function msgToStep(message) {
  if (!message) return null;
  const lower = message.toLowerCase();
  return STEP_MAP.find(s => lower.includes(s.match)) || null;
}

const BRContext = createContext(null);

export function BRProvider({ children }) {
  const [syncing,   setSyncing]   = useState(false);
  const [syncError, setSyncError] = useState('');
  const [syncDone,  setSyncDone]  = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [stepLabel, setStepLabel] = useState('');
  const [brLatest,  setBrLatest]  = useState(null);

  // AuditPage registers its "open verify dialog" handler here.
  const [verifyPoId,    setVerifyPoId]    = useState(null);
  const [verifyHandler, setVerifyHandler] = useState(null);

  const pollRef = useRef(null);

  // Load last-synced metadata on mount
  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/bamboo-rose/latest`)
      .then(r => setBrLatest(r.data))
      .catch(() => {});
  }, []);

  // Auto-sync on every app load / page reload.
  // The component mounts fresh each time, so this always fires once at startup.
  // The manual "Sync BR" button also calls handleSync() — the guard inside
  // (if (syncing) return) prevents double-starts.
  useEffect(() => {
    handleSync();
    // Intentionally empty deps — run exactly once on mount, not on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup poll on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleSync = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncError('');
    setSyncDone(false);
    setProgress(3);
    setStepLabel('Queuing job…');

    try {
      const res   = await axios.post(`${API_BASE_URL}/api/bamboo-rose/extract`);
      const jobId = res.data.job_id;

      pollRef.current = setInterval(async () => {
        try {
          const { data } = await axios.get(
            `${API_BASE_URL}/api/bamboo-rose/extract-status/${jobId}`
          );

          const step = msgToStep(data.message);
          if (step) {
            setProgress(step.pct);
            setStepLabel(step.label);
          } else if (data.message) {
            setStepLabel(data.message);
          }

          if (data.status === 'done') {
            clearInterval(pollRef.current);
            setProgress(100);
            setStepLabel(`Done — ${data.row_count ?? '?'} POs synced`);
            setSyncing(false);
            setSyncDone(true);
            const lat = await axios.get(`${API_BASE_URL}/api/bamboo-rose/latest`);
            setBrLatest(lat.data);
          } else if (data.status === 'error') {
            clearInterval(pollRef.current);
            setSyncing(false);
            setProgress(0);
            setStepLabel('');
            setSyncError(data.message || 'Bamboo Rose sync failed');
          }
        } catch { /* transient poll error — keep going */ }
      }, 2000);
    } catch (err) {
      setSyncing(false);
      setProgress(0);
      setStepLabel('');
      setSyncError(err.response?.data?.detail || err.message || 'Failed to start BR sync');
    }
  }, [syncing]);

  /** Called by AuditPage on mount to wire the header's Verify BR button. */
  const registerVerify = useCallback((poId, handler) => {
    setVerifyPoId(poId);
    setVerifyHandler(() => handler);  // wrap in arrow so setState treats it as a value
  }, []);

  /** Called by AuditPage on unmount to remove the header button. */
  const unregisterVerify = useCallback(() => {
    setVerifyPoId(null);
    setVerifyHandler(null);
  }, []);

  return (
    <BRContext.Provider value={{
      syncing, syncError, syncDone, progress, stepLabel, brLatest,
      handleSync,
      verifyPoId, verifyHandler, registerVerify, unregisterVerify,
    }}>
      {children}
    </BRContext.Provider>
  );
}

export const useBR = () => {
  const ctx = useContext(BRContext);
  if (!ctx) throw new Error('useBR must be used inside <BRProvider>');
  return ctx;
};
